import * as React from "react";
import { ITaskAttrs, ITask, buildPredecessorsMap, IPredecessorInfo, PredecessorType, PredecessorTypeMetadata, isExternalPredecessor } from "../../entities/Subentities";

import { Validator, IValidator, ValidationBuilder } from "../../validation";
import { nameof } from "../../store/services/metadataService";
import * as Metadata from '../../entities/Metadata';
import TaskStartDueDatePickerInput from "../common/inputs/TaskStartDueDatePickerInput";
import SliderInput from '../common/inputs/SliderInput';
import GroupDropdown from "../common/inputs/GroupDropdown";
import OptionsPicker, { Option } from "../common/inputs/OptionsPicker";
import * as utils from '../common/timeline/utils';
import { Dictionary, IBaseEntity, IUserInfo, MaybeDate, ProgressCalculationType, TaskType } from "../../entities/common";
import NumberInput from "../common/inputs/NumberInput";
import { applyFixedUnitsRecalc, applyPredecessors, calculateDueDate, deriveFlexibleUnits, DURATION_COMPARISON_EPSILON, getAssigneeCalendars, getRealDuration, getSpanOwners, handleDuration, pinSpanToDictatedDue, previewFixedUnitsEffortEdit, previewFlexibleEffortEdit, reprojectsOntoAssigneeDays, SchedulingInputs } from "../utils/duration";
import { FormatDate, HUNDRED_PCT, MaxDateConst, MaxDuration, MinDateConst, adjustProgress, formatValue, notUndefined, toDate, toDictionaryById, toUniversalDateTime } from "../utils/common";
import { css, DirectionalHint } from "office-ui-fabric-react";
import type { ProjectInfo } from "../../store/ProjectsListStore";
import { IInputProps } from "../common/interfaces/IInputProps";
import type { Resource } from "../../store/ResourcesListStore";
import AssigneePickerInput from "../common/inputs/AssigneePickerInput";
import { searchPathMap } from "../../entities/Metadata";
import type { PanelEdits } from "../common/SubentityPanel/UpdatePanel";
import { CalendarDataSet } from "../../store/calendar/common";

// StartDate / DueDate are FieldType.UniversalDateTime; date pickers may emit null (clear) or invalid input.
// Bug 17135: the working-day boundaries come from the TASK calendar (project + assignees) — the same union the
// duration walks measure a day with, and the same rule the server applies (ToDayStartFor/ToDayEndFor/
// ToUniversalDueDate). Resolving them on the project calendar ALONE agrees only while every assignee work week is
// a SUBSET of the project's; for a DISJOINT one (a Sat–Sun resource) the project calendar has no working time for
// the very day the picker offered, so a Due picked on that day was stamped with the START's time-of-day (the
// "non-working day" rule) and the reconciled span then measured 0 — Due=Start, Duration 0.
const stampStart = (value: any, assigneeCalendars: CalendarDataSet[], calendar: CalendarDataSet, useResourceCalendar: boolean): any => {
    if (value == null) {
        return value;
    }
    const typed = toDate(value);
    if (!typed) {
        return value;
    }
    // The picker only OFFERS working days, but the box can be TYPED into as well, and a typed one used to
    // travel exactly as entered while the panel displayed something else: the display derivation snaps a non-working
    // Start onto the next working day (adjustStartDueDatesToAssigneeCalendars) and the payload carried the raw edit.
    // The server does not snap a REQUESTED Start — it rejects it (EnsureSchedulingRulesMet) — so typing a Saturday
    // showed the Monday the picker would have offered and saved the Saturday. Snap here, where the edit is recorded,
    // and `changes`, the display and the payload are one date again. Off with resource calendars, where a non-working
    // Start is allowed and neither side snaps.
    const date = useResourceCalendar && !typed.isWorkingDay(...assigneeCalendars)
        ? typed.addWorkingDays(1, ...assigneeCalendars)
        : typed;
    return date.setTimeOfDay(utils.dayStartMinutesFor(date, assigneeCalendars, calendar)).toUniversalIsoDateTimeString();
};

const stampDue = (value: any, assigneeCalendars: CalendarDataSet[], calendar: CalendarDataSet, duration?: number | null, startDate?: any): any => {
    if (value == null) {
        return value;
    }
    const date = toDate(value);
    if (!date) {
        return value;
    }
    if (duration === 0) {
        return date.setTimeOfDay(utils.dayStartMinutesFor(date, assigneeCalendars, calendar)).toUniversalIsoDateTimeString();
    }
    const start = toUniversalDateTime(startDate);
    // A day NOBODY works keeps the Start's time-of-day (Task 16785); a day the assignees work takes its day end.
    if (start && !utils.dayHasWorkingTime(date, assigneeCalendars, calendar)) {
        return date.setTimeOfDay(start.getHours() * 60 + start.getMinutes()).toUniversalIsoDateTimeString();
    }
    return date.setTimeOfDay(utils.dayEndMinutesFor(date, assigneeCalendars, calendar)).toUniversalIsoDateTimeString();
};

// Bug 17371 – the membership change as it stood BEFORE the Units were typed: a resource ALREADY on the task keeps
// the Units it is stored with, and only a fresh pick carries none — which is what makes this first step the same
// span-free payload the engine would have got had the two edits been saved separately. Blanking every assignee
// instead recomputed the survivors' Effort at the 100% default.
const asStoredUnits = (assignees: IUserInfo[], stored: IUserInfo[] | undefined): IUserInfo[] =>
    assignees.map(a => ({ ...a, units: (stored ?? []).find(p => p.id === a.id)?.units }));

// Mirror of the engine's span reconciliation for a MANUAL task: each assignee's rows are an even split over
// ceil(Duration) days of their OWN calendar, and the span follows the latest of them (extending always, contracting
// only while nothing else claims the Due).
const extendSpanToAssigneeRows = (
    attrs: Partial<ITaskAttrs>,
    requestedDuration: number | null | undefined,
    spanStart: Date,
    spanDue: Date,
    mayContract: boolean,
    scheduling: SchedulingInputs): Partial<ITaskAttrs> | undefined => {
    const { calendar, resources, calendarsById, useResourceCalendar } = scheduling;
    // The row count is ceil() of the Duration the ROWS are built from, which is the one this step REQUESTED — not the
    // one reconciliation left behind. They differ, and using the latter double-extends: handleDuration declines to
    // shrink a span it cannot measure at the new value (Duration 3 → 2 over a Mon–Wed + Wed–Fri pair comes back
    // 3d/Thu, which is already what the engine stores), and re-walking THREE days from there ran to the following
    // Monday. The engine walks the requested 2 and then extends, landing on the same 3d.
    const rowBasis = requestedDuration ?? attrs.Duration;
    const rowDays = rowBasis != null && rowBasis > 0 ? Math.max(1, Math.ceil(rowBasis)) : undefined;
    if (!rowDays) {
        return undefined;
    }

    const cals = getAssigneeCalendars(useResourceCalendar, attrs.AssignedTo, calendar, resources, calendarsById);
    const latest = utils.max([spanDue, ...cals.map(c => calculateDueDate(spanStart, rowDays, calendar, [c]))]) ?? spanDue;
    const spanChanged = latest > spanDue || (mayContract && latest < spanDue && latest >= spanStart);
    return spanChanged
        ? { DueDate: latest.toUniversalIsoDateTimeString(), Duration: utils.getDurationBetween(spanStart, latest, cals, calendar) }
        : undefined;
};

// Bug 17138 – can a Fixed Units span (Duration/Due) be RE-DERIVED after a superseding Effort/Start edit deletes it?
// Only from assignee capacity: with no assignee there is nothing to divide the Effort by, so BOTH the server's
// ApplyFixedUnitsRules and previewFixedUnitsEffortEdit bail out on an unassigned task and the deleted span edit is
// replaced by nothing — the display and the save fall back to the STORED span (a Duration re-typed 2 → 3 reverted to
// 2d the moment an Effort was entered) or, on the create panel, to the fabricated 1-day new-task default (a typed
// 5d/Aug 7 collapsed onto the Start). In that case the user's span edit is the only span there is, so KEEP it: an
// unassigned Fixed Units task has no Effort↔Duration link for the extra Duration in the payload to disturb (see the
// assigneeCalendars.Count == 0 guards in TaskWorkloadHandler). Read from the DISPLAY state, so a resource added in
// this same panel session (not yet saved) already counts.
const fixedUnitsSpanCanBeReDerived = (state: ITask): boolean => (state.attributes.AssignedTo?.length ?? 0) > 0;

// Mirror of TaskWorkloadHandler.IsStoredScheduleRejectedFor: would the engine refuse this task's STORED Start/Due/
// Duration for the given set of calendars? Either the Start is not a working day for any of them, or the span no
// longer holds the Duration when measured on them. It is the condition the engine gates its assignee-change span
// repair on, so the panel has to ask the same question to preview the same dates.
const storedScheduleRejectedFor = (attrs: ITaskAttrs, calendars: CalendarDataSet[], globalCalendar: CalendarDataSet): boolean => {
    const start = toUniversalDateTime(attrs.StartDate);
    const due = toUniversalDateTime(attrs.DueDate);
    if (!start || !due || attrs.Duration == null) {
        return false;
    }
    if (!calendars.some(c => start.isWorkingDay(c))) {
        return true;
    }
    const zeroDayMilestone = attrs.Duration === 0 && start.getBeginOfDay().getTime() === due.getBeginOfDay().getTime();
    return Math.abs(utils.getDurationBetween(start, due, calendars, globalCalendar) - attrs.Duration) > DURATION_COMPARISON_EPSILON
        && !zeroDayMilestone;
};

// The half of the condition above that a payload CARRYING a span cannot recover from (Bug 17159): a Start on a day
// none of these calendars works. The other half — a span that no longer measures the Duration — is repaired by any
// Duration in the payload, since the engine re-derives the Due from Start + Duration on these very calendars.
const startNotWorkedBy = (attrs: ITaskAttrs, calendars: CalendarDataSet[]): boolean => {
    const start = toUniversalDateTime(attrs.StartDate);
    return !!start && !calendars.some(c => start.isWorkingDay(c));
};

// True when two assignee lists have the SAME set of resource ids (Units/order ignored) — i.e. no add/remove.
const sameAssigneeIds = (a?: IUserInfo[], b?: IUserInfo[]): boolean => {
    const bIds = new Set((b ?? []).map(_ => _.id));
    const aList = a ?? [];
    return aList.length === bIds.size && aList.every(_ => bIds.has(_.id));
};

export const ASSIGNMENT_UNITS_NEGATIVE_ERROR = "Units cannot be negative";

const baseValidatorsBuilder = () => ({
    [nameof<ITaskAttrs>("Progress")]: (state: ITask, field: Metadata.Field): IValidator => {
        const validatorBuilder = Validator.new();
        if (field.type === Metadata.FieldType.Decimal) {
            validatorBuilder.decimal();
        }
        else if (field.type === Metadata.FieldType.Integer) {
            validatorBuilder.int32();
        }
        if (field.settings?.required) {
            validatorBuilder.required();
        }
        validatorBuilder.min(field.settings?.minValue).max(field.settings?.maxValue).step(field.settings?.minValue, field.settings?.step)
        return validatorBuilder.build();
    }
});

export const validatorsBuilder = (
    calendar: CalendarDataSet,
    resources: Dictionary<Resource>,
    calendarsById: Dictionary<CalendarDataSet>,
    useResourceCalendar: boolean,
    tasksLoader: () => LoadedTask[] | undefined,
    shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay: boolean = true) => {
    return {
        ...baseValidatorsBuilder(),
        [nameof<ITaskAttrs>("StartDate")]: (state: ITask, field: Metadata.Field): IValidator => {
            const validatorBuilder = Validator.new().date();
            const dictatedDates = TaskScheduleCalculator.GetTaskDictatedDates(state, toDictionaryById(tasksLoader() ?? []), calendar, useResourceCalendar, resources, calendarsById, shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay);
            if (dictatedDates?.startDate) {
                validatorBuilder.required("Required when ToStart predecessor set");
            } else if (field.settings?.required) {
                validatorBuilder.required();
            }
            validatorBuilder.dateIsLessThenOrEqual(state.attributes.DueDate);
            return validatorBuilder.dateIsGreaterThenOrEqual(dictatedDates?.startDate, messages.predecessorDate).build();
        },
        [nameof<ITaskAttrs>("DueDate")]: (state: ITask, field: Metadata.Field): IValidator => {
            const validatorBuilder = Validator.new().date();
            const dictatedDates = TaskScheduleCalculator.GetTaskDictatedDates(state, toDictionaryById(tasksLoader() ?? []), calendar, useResourceCalendar, resources, calendarsById, shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay);

            setDueDateRequiredValidator(validatorBuilder, state, field, dictatedDates);

            validatorBuilder.customDate((date) => {
                const calendars = getAssigneeCalendars(useResourceCalendar, state.attributes.AssignedTo, calendar, resources, calendarsById);
                const startDate = toUniversalDateTime(state.attributes.StartDate);
                const dueDate = toUniversalDateTime(state.attributes.DueDate);

                if (date && startDate && dueDate && state.attributes.Duration !== undefined && state.attributes.Duration !== null) {
                    const actualDuration = utils.getDurationBetween(startDate, dueDate, calendars, calendar);
                    if (Math.abs(actualDuration - state.attributes.Duration) > DURATION_COMPARISON_EPSILON) {
                        return false;
                    }
                }
                return true;
            }, "Dates mismatch due to calendar changes");
            return setDueDateIsGreaterValidator(validatorBuilder, state, dictatedDates).build();
        },
        [nameof<ITaskAttrs>("Duration")]: (state: ITask, field: Metadata.Field): IValidator => {
            // US 16555: Duration is a decimal number of days (up to 2 dp); validate like custom decimal fields.
            const validatorBuilder = Validator.new().decimal();
            if (field.settings?.required) {
                validatorBuilder.required();
            }
            return validatorBuilder.min(0).max(MaxDuration).build();
        },
        [nameof<ITaskAttrs>("Predecessor")]: (state: ITask, field: Metadata.Field): IValidator => {
            const tasksMap = toDictionaryById(tasksLoader() ?? []);
            const validatorBuilder = Validator
                .new()
                .custom((_: ITaskAttrs["Predecessor"]) => {
                    const predecessors = (_ || []).map(p => tasksMap[p.id]).filter(notUndefined);
                    return predecessors.every(p => isAllowedAsPredecessorByHierarchy(state, p));
                }, messages.notAllowedByHierarchyRemoveIt);
            return validatorBuilder.build();
        },
        [nameof<ITaskAttrs>("AssignedTo")]: (): IValidator => Validator.new()
            .custom((_: IUserInfo[] | undefined) => (_ ?? []).every(a => a.units == null || a.units >= 0), ASSIGNMENT_UNITS_NEGATIVE_ERROR)
            .build()
    };
};

export const inlineValidatorsBuilder = (
    calendar: CalendarDataSet,
    resources: Dictionary<Resource>,
    calendarsById: Dictionary<CalendarDataSet>,
    useResourceCalendar: boolean,
    tasksLoader: () => LoadedTask[] | undefined,
    taskFields: Metadata.Field[],
    shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay: boolean = true) => {
    return {
        ...baseValidatorsBuilder(),
        [nameof<ITaskAttrs>("StartDate")]: (state: ITask, field: Metadata.Field): IValidator => {
            const tasksMap = toDictionaryById(tasksLoader() ?? []);
            const validatorBuilder = Validator.new().date();
            const dictatedDates = TaskScheduleCalculator.GetTaskDictatedDates(state, tasksMap, calendar, useResourceCalendar, resources, calendarsById, shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay);
            if (dictatedDates?.startDate) {
                validatorBuilder.required("Required when ToStart predecessor set");
            } else if (field.settings?.required) {
                validatorBuilder.required();
            }
            const durationName = Metadata.getLabel(taskFields.filter(_ => _.name === nameof<ITaskAttrs>("Duration"))[0]);
            validatorBuilder.customDate((date) => {
                const extra = handleDuration(state.attributes, { StartDate: date }, calendar, resources, calendarsById, useResourceCalendar, true, dictatedDates);
                return extra?.Duration === undefined || extra?.Duration === null || extra.Duration <= MaxDuration;
            }, `${durationName} must be less than or equal to 10000 d`);
            return validatorBuilder.dateIsGreaterThenOrEqual(dictatedDates?.startDate, messages.predecessorDate).build();
        },
        [nameof<ITaskAttrs>("DueDate")]: (state: ITask, field: Metadata.Field): IValidator => {
            const tasksMap = toDictionaryById(tasksLoader() ?? []);
            const validatorBuilder = Validator.new().date();
            const dictatedDates = TaskScheduleCalculator.GetTaskDictatedDates(state, tasksMap, calendar, useResourceCalendar, resources, calendarsById, shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay);

            setDueDateRequiredValidator(validatorBuilder, state, field, dictatedDates);
            const durationName = Metadata.getLabel(taskFields.filter(_ => _.name === nameof<ITaskAttrs>("Duration"))[0]);
            validatorBuilder.customDate((date) => {
                const extra = handleDuration(state.attributes, { DueDate: date }, calendar, resources, calendarsById, useResourceCalendar, false, dictatedDates);
                return extra?.Duration === undefined || extra?.Duration === null || extra.Duration <= MaxDuration;
            }, `${durationName} must be less than or equal to 10000 d`);
            return setDueDateIsGreaterValidator(validatorBuilder, state, dictatedDates).build();
        },
        [nameof<ITaskAttrs>("Duration")]: (state: ITask, field: Metadata.Field): IValidator => {
            // US 16555: Duration is a decimal number of days (up to 2 dp).
            const validatorBuilder = Validator.new().decimal().min(0).max(MaxDuration);
            const assigneeCalendars = getAssigneeCalendars(useResourceCalendar, state.attributes.AssignedTo, calendar, resources, calendarsById);
            const dictatedDates = TaskScheduleCalculator.GetDictatedDates(state.attributes.Predecessor, toDictionaryById(tasksLoader() ?? []), calendar, assigneeCalendars, shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay, state);

            if (field.settings?.required) {
                validatorBuilder.required();
            }
            const startDateName = Metadata.getLabel(taskFields.filter(_ => _.name === nameof<ITaskAttrs>("StartDate"))[0]);
            validatorBuilder.custom((duration) => {
                const extra = handleDuration(state.attributes, { Duration: duration }, calendar, resources, calendarsById, useResourceCalendar, true, dictatedDates, reprojectsOntoAssigneeDays(state));
                return extra?.StartDate === undefined || extra?.StartDate === null || toDate(extra.StartDate)! > MinDateConst;
            }, `${startDateName} should be greater or equal ${FormatDate(MinDateConst)}`);
            const dueDateName = Metadata.getLabel(taskFields.filter(_ => _.name === nameof<ITaskAttrs>("DueDate"))[0]);
            validatorBuilder.custom((duration) => {
                const extra = handleDuration(state.attributes, { Duration: duration }, calendar, resources, calendarsById, useResourceCalendar, false, dictatedDates, reprojectsOntoAssigneeDays(state));
                return extra?.DueDate === undefined || extra?.DueDate === null || toDate(extra.DueDate)! < MaxDateConst;
            }, `${dueDateName} should be less or equal ${FormatDate(MaxDateConst)}`);
            return validatorBuilder.build();
        },
        [nameof<ITaskAttrs>("IsMilestone")]: (state: ITask, field: Metadata.Field): IValidator => {
            const dueDateName = Metadata.getLabel(taskFields.filter(_ => _.name === nameof<ITaskAttrs>("DueDate"))[0]);
            return Validator.new()
                .custom((flag) => !flag || !!state.attributes.DueDate, `${dueDateName} is required`)
                .required()
                .build();
        },
    };
}

const setDueDateRequiredValidator = (validatorBuilder: ValidationBuilder, state: ITask, field: Metadata.Field, dictatedDates: DictatedDates) => {
    if (state.attributes.IsMilestone) {
        validatorBuilder.required("Required for Milestone");
    } else if (dictatedDates?.dueDate) {
        validatorBuilder.required("Required when ToFinish predecessor set");
    } else if (field.settings?.required) {
        validatorBuilder.required();
    }
    return validatorBuilder;
}

const setDueDateIsGreaterValidator = (validatorBuilder: ValidationBuilder, state: ITask, dictatedDates: DictatedDates) => {
    const minDueDate = utils.maxBy([
        { date: dictatedDates?.dueDate, message: messages.predecessorDate },
        { date: state.attributes.StartDate }
    ], _ => _.date);
    return validatorBuilder.dateIsGreaterThenOrEqual(minDueDate?.date, minDueDate?.message);
}

export const messages = {
    predecessorDate: 'Date should be set to {0}, per predecessor',
    notAllowedByHierarchy: 'Dependency between subtasks and their summary tasks cannot be created.',
    notAllowedByHierarchyRemoveIt: "Dependency between subtasks and their summary tasks is not allowed. Remove it to save the task."
}

export type LoadedTask = {
    id: string,
    attributes: { Name: string, StartDate?: MaybeDate, DueDate?: MaybeDate, Duration?: number | null, IsMilestone?: boolean, Predecessor?: IPredecessorInfo[] },
    hierarchy?: { parentIds?: string[] }
};

export const rendersBuilder = (
    project: ProjectInfo,
    groups: Metadata.Group[],
    calendar: CalendarDataSet,
    resources: Dictionary<Resource>,
    calendarsById: Dictionary<CalendarDataSet>,
    useResourceCalendar: boolean,
    urlBuilder: (task: { id: string }) => string | undefined,
    tasksLoader: () => LoadedTask[] | undefined,
    shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay: boolean = true
) => ({
    [nameof<ITaskAttrs>("AssignedTo")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: IValidator): JSX.Element | null =>    {
        return <AssigneePickerInput {...props}
            searchUrl={`api/${searchPathMap[field.type as keyof typeof searchPathMap]}/find`}
            multichoice={field.settings?.multichoice}
            validator={validator}
            onEditComplete={() => props.onEditComplete(null)}/>
    },
    [nameof<ITaskAttrs>("StartDate")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: Validator): JSX.Element | null => {
        const taskMap = toDictionaryById(tasksLoader() ?? []);
        const assigneeCalendars = getAssigneeCalendars(useResourceCalendar, state.attributes.AssignedTo, calendar, resources, calendarsById);
        const dictatedDates = TaskScheduleCalculator.GetDictatedDates(state.attributes.Predecessor, taskMap, calendar, assigneeCalendars, shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay, state);
        return <TaskStartDueDatePickerInput
            {...props}
            assigneeCalendars={assigneeCalendars}
            useResourceCalendar={useResourceCalendar}
            inputProps={{ readOnly: field.isReadonly }}
            validator={validator}
            showTime
            minDate={isTaskPredecessorHierarchyValid(state, taskMap) ? dictatedDates?.startDate : undefined}
            onChanged={props.onChanged ? (value) => {
                // Send only the (day-start stamped) Start; the server re-derives Duration/Due and the central
                // derivation previews them. Last-change-wins: a Start move supersedes a prior Due edit (Due re-
                // derives). For Fixed Units it also supersedes a prior Duration edit (Effort is preserved and
                // Duration re-derived — a stale Duration must not be sent, or the server recomputes Effort); for
                // Flexible it supersedes a prior per-assignee Units override (Units re-derive over the moved span).
                // A units-override clear only drops AssignedTo when it is units-only — a pending membership add stays.
                // Bug 17138 – the Duration supersede needs assignee capacity to re-derive from, exactly like the Effort
                // field's (fixedUnitsSpanCanBeReDerived); unassigned, it would drop a just-typed Duration for the stored one.
                const supersede = state.taskType === TaskType.FixedUnits
                    ? (fixedUnitsSpanCanBeReDerived(state) ? { DueDate: undefined, Duration: undefined } : { DueDate: undefined })
                    : { DueDate: undefined, AssignedTo: undefined };
                props.onChanged?.(stampStart(value, assigneeCalendars, calendar, useResourceCalendar), supersede);
            } : undefined}
            onEditComplete={() => props.onEditComplete(null)}
        />;
    },
    [nameof<ITaskAttrs>("DueDate")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: IValidator): JSX.Element | null => {
        const taskMap = toDictionaryById(tasksLoader() ?? []);
        const assigneeCalendars = getAssigneeCalendars(useResourceCalendar, state.attributes.AssignedTo, calendar, resources, calendarsById);
        const dictatedDates = TaskScheduleCalculator.GetDictatedDates(state.attributes.Predecessor, taskMap, calendar, assigneeCalendars, shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay, state);
        const minDueDate = utils.max([isTaskPredecessorHierarchyValid(state, taskMap) ? dictatedDates?.dueDate : undefined, state.attributes.StartDate]);
        return <TaskStartDueDatePickerInput {...props}
            assigneeCalendars={assigneeCalendars}
            useResourceCalendar={useResourceCalendar}
            inputProps={{ readOnly: field.isReadonly }}
            validator={validator}
            showTime
            minDate={minDueDate}
            onChanged={props.onChanged ? (value) => {
                const stamped = stampDue(value, assigneeCalendars, calendar, state.attributes.Duration, state.attributes.StartDate);
                // A Due edit is a RESIZE (keep Start, re-derive Duration) — but `{DueDate}` ALONE is ambiguous and the
                // server reads it as a MOVE: SetStartDateByDueDateAndDurationIfNoStartDateInUpdate preserves Duration
                // and shifts Start instead (TaskWorkloadHandler.cs). So this is the one date edit that must send the
                // whole reconciled triple: with Start+Due+Duration all present, AdjustScheduleDataUpdates drops the
                // Due and re-derives it from the client's Start+Duration, honouring the resize. (The inline grid /
                // drag path already sends the triple — that is why it stays correct.) Start and Duration edits are
                // NOT ambiguous (server and client both move), so they keep sending the primary value only.
                const resized = handleDuration(state.attributes, { DueDate: stamped }, calendar, resources, calendarsById, useResourceCalendar, false, dictatedDates);
                // Last-change-wins: the reconciled Duration/Start above overwrite any earlier Duration/Start edit; on
                // a Flexible task a prior per-assignee Units override is superseded (Units re-derive over the new
                // span). A pending membership add is never cleared.
                const extra = state.taskType === TaskType.Flexible ? { ...resized, AssignedTo: undefined } : { ...resized };
                props.onChanged?.(stamped, extra);
            } : undefined}
            onEditComplete={() => props.onEditComplete(null)}
        />;
    },
    [nameof<ITaskAttrs>("Duration")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: IValidator): JSX.Element | null => {
        return <NumberInput {...props} inputProps={toNumberInputProps(field)} validator={validator} format={field.settings?.format} allowForceUpdate
            onChanged={undefined} onEditComplete={(value) => {
                // Send only the raw Duration; the server derives Due/Effort/Units and the central derivation previews
                // them. Last-change-wins: editing Duration supersedes a prior Due edit (Due re-derives) and, on a
                // Flexible task, a prior per-assignee Units override (Units re-derive from the new Duration). A pending
                // membership add is never cleared.
                const supersede = state.taskType === TaskType.Flexible ? { DueDate: undefined, AssignedTo: undefined } : { DueDate: undefined };
                props.onChanged?.(value, supersede);
                props.onEditComplete(value, supersede);
            }}
        />;
    },
    [nameof<ITaskAttrs>("Progress")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: IValidator): JSX.Element | null => {
        const sliderProps = {
            min: field.settings?.minValue,
            max: field.settings?.maxValue,
            step: field.settings?.step,
            defaultValue: field.settings?.defaultValue,
            className: field.settings?.className,
            readOnly: field.isReadonly
        };
        return <SliderInput {...props} inputProps={sliderProps} validator={validator}
            onEditComplete={(value: number | null) => {
                const extra = TaskProgressCalculator.RecalculateByProgress(project, state, value);
                props.onChanged?.(value, extra);
                props.onEditComplete(value, extra);
            }}
        />;
    },
    [nameof<ITaskAttrs>("Effort")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: IValidator): JSX.Element | null => {
        return <NumberInput {...props} inputProps={toNumberInputProps(field)} validator={validator} format={field.settings?.format} allowForceUpdate
            onChanged={undefined} onEditComplete={(value) => {
                // Send Effort + its Progress/Remaining consequences only. The server derives Duration/Due (Fixed
                // Units) and re-derives Units (Flexible); the central derivation previews both. Never send derived
                // Duration/Due — that flips the server into recompute-Effort (see the duration.ts header).
                // Last-change-wins: an Effort edit supersedes a prior Duration/Due edit on Fixed Units (they re-derive
                // from Effort), or a prior per-assignee Units override on Flexible (Duration is kept, Units re-derive).
                // Superseding DELETES the Duration/Due edit, so it needs something to fall back on — assignee capacity
                // to re-derive the span from (fixedUnitsSpanCanBeReDerived). WITH assignees it must supersede:
                // `changes` is the save payload, and an Effort edit has to reach the server WITHOUT a Duration, or
                // ApplyFixedUnitsRules recomputes Effort from that Duration (40h) instead of taking the
                // first-assignment path that keeps the entered 120h.
                const supersede: Partial<ITaskAttrs> | undefined = state.taskType === TaskType.FixedUnits
                    ? (fixedUnitsSpanCanBeReDerived(state) ? { Duration: undefined, DueDate: undefined } : undefined)
                    : { AssignedTo: undefined };
                const extra = { ...TaskProgressCalculator.RecalculateByEffort(project, state, value), ...supersede };
                props.onChanged?.(value, extra);
                props.onEditComplete(value, extra);
            }}
        />;
    },
    [nameof<ITaskAttrs>("CompletedWork")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: IValidator): JSX.Element | null => {
        return <NumberInput {...props} inputProps={toNumberInputProps(field)} validator={validator} format={field.settings?.format} allowForceUpdate
            onChanged={undefined} onEditComplete={(value) => {
                const extra = TaskProgressCalculator.RecalculateByCompletedWork(project, state, value);
                props.onChanged?.(value, extra);
                props.onEditComplete(value, extra);
            }}
        />;
    },
    [nameof<ITaskAttrs>("RemainingWork")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: IValidator): JSX.Element | null => {
        return <NumberInput {...props} inputProps={toNumberInputProps(field)} validator={validator} format={field.settings?.format} allowForceUpdate
            onChanged={undefined} onEditComplete={(value) => {
                const extra = TaskProgressCalculator.RecalculateByRemainingWork(project, state, value);
                props.onChanged?.(value, extra);
                props.onEditComplete(value, extra);
            }}
        />;
    },
    [nameof<ITaskAttrs>("Group")]: (props: IInputProps, state: any, field: Metadata.Field): JSX.Element | null =>
        <GroupDropdown dropdownInputProps={props} value={props.value} groups={groups} />,
    [nameof<ITaskAttrs>("Predecessor")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: IValidator): JSX.Element | null => {
        const tasks = tasksLoader();
        const tasksMap = toDictionaryById(tasks ?? []);
        const selectedItems = props.value ? props.value.map((_: IPredecessorInfo) => ({
            key: _.id,
            text: isExternalPredecessor(_)
                ? `${_.name} (${_.projectName ?? _.projectId}) (${PredecessorTypeMetadata[_.type].abbreviation}${_.lag < 0 ? "" : "+"}${formatValue(_.lag, Metadata.FormatType.Days)})`
                : `${_.name} (${PredecessorTypeMetadata[_.type].abbreviation}${_.lag < 0 ? "" : "+"}${formatValue(_.lag, Metadata.FormatType.Days)})`,
            url: isExternalPredecessor(_) ? undefined : urlBuilder(_),
            data: {
                predecessor: _,
            }
        })) : [];

        const onChange = props.onChanged
            ? (newOptions?: Option[]) => {
                const predecessorsData = newOptions?.length
                    ? (newOptions as unknown as { data: { predecessor: IPredecessorInfo } }[]).map(_ => _.data)
                    : [];
                const predecessors = predecessorsData?.map(_ => _.predecessor);
                // Send only the predecessors; the server applies the dependency and re-derives dates/Effort/Units,
                // and the central derivation previews them.
                props.onChanged!(predecessors);
            } : undefined

        return <>
            <OptionsPicker
                className={css("predecessors", validator?.isValid(props.value) ? undefined : "invalid")}
                disabled={props.readOnly || field.isReadonly}
                pickerCalloutProps={{ directionalHint: DirectionalHint.bottomRightEdge }}
                onResolveSuggestions={(filter, selectedOptions) => {
                    const predecessorsMap = buildPredecessorsMap(tasks);
                    return tasks
                        ?.filter(_ => (!state.id || _.id != state.id && !predecessorsMap[_.id]?.[state.id])
                            && isAllowedAsPredecessorByHierarchy(state, _)
                            && _.attributes.Name.toLowerCase().includes(filter.toLowerCase())
                            && !selectedOptions?.find(i => _.id === i.key))
                        ?.map<Option>(_ => ({
                            key: _.id,
                            text: _.attributes.Name,
                            url: urlBuilder(_),
                            data: {
                                predecessor: {
                                    id: _.id,
                                    name: _.attributes.Name,
                                    lag: 0,
                                    type: PredecessorType.FinishToStart
                                }
                            }
                        })) || [];
                }}
                onChange={onChange}
                selectedItems={selectedItems}
            />
            <div className="error-message">{validator?.getErrorMessage(props.value)}</div>
        </>
    }
})

// US 15956 – the panel's single, drift-free DISPLAY derivation. Given the ORIGINAL task `entity` and the user's
// raw `changes` (primary edits only — never client-derived dates/units), it returns the scheduling overlay the
// server will produce: reconciled Start/Due/Duration, Fixed Units Effort (or Flexible per-assignee Units), and the
// off-day-assignee span extension. It is recomputed FRESH from (entity + changes) on every render, so it can never
// drift (adding resources one by one always re-derives from the original span, not a previously-extended one).
// DISPLAY ONLY: `changes` is the minimal save payload and the server derives these authoritatively (verified).
// Reuses handleDuration / applyFixedUnitsRecalc / applyPredecessors / deriveFlexibleUnits / previewFixedUnitsEffortEdit.
/**
 * Bug 17209 – does this Units edit REDISTRIBUTE the Effort pool rather than just restate one assignment?
 *
 * A Units edit is otherwise left exactly as the user typed it, which is right for the assignee they touched and wrong
 * for everyone else the moment one is set to 0%: that assignee contributes nothing and carries no rows, so the engine
 * (ApplyZeroUnitsEdit) moves the WHOLE pool onto the assignees still above 0% and re-derives their Units — a 2d/32h
 * pair at 100% each leaves the survivor at 200%. The panel kept showing 100% and only the save revealed the 200%.
 *
 * Deliberately narrow: it asks about a 0%, not about "which assignee was typed". Telling a typed Units from a derived
 * one is not answerable from the list alone — on a Flexible task the panel DISPLAYS derived Units, so the picker's
 * list carries them back and "different Units" means nothing.
 */
const redistributesFromZeroUnits = (assignees: IUserInfo[] | undefined): boolean => {
    const isZeroed = (a: IUserInfo) => (a.units ?? HUNDRED_PCT) <= 0;
    return !!assignees && assignees.some(isZeroed) && assignees.some(a => !isZeroed(a));
};

/**
 * The two cases where the derived rate says nothing about an assignee, so it keeps the Units it already has:
 *  • an explicit 0% — that assignee contributes no effort and gets no scheduled days, so the rate the derivation
 *    measures against its raw calendar capacity describes work it will never do (it would come back at the SURVIVOR's
 *    200%, silently undoing the edit that was just made);
 *  • a NULL derivation — "no capacity to measure a rate against", which carries no information at all. Mirrors the
 *    server's ApplyFlexibleDerivedUnits, which skips writing a null over an assignee that already has Units.
 */
const keepUnitsTheDerivationCannotSpeakFor = (before: IUserInfo[], derived: IUserInfo[]): IUserInfo[] =>
    derived.map((a, i) => {
        const prior = before[i]?.units;
        return prior != null && (prior <= 0 || a.units == null) ? { ...a, units: prior } : a;
    });

export const buildDeriveDisplayAttributes = (
    project: ProjectInfo,
    calendar: CalendarDataSet,
    resources: Dictionary<Resource>,
    calendarsById: Dictionary<CalendarDataSet>,
    useResourceCalendar: boolean,
    tasksLoader: () => LoadedTask[] | undefined,
    shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay: boolean = true,
    /**
     * Bug 17380 – what each assignee holds of the task Effort, from its persisted assignment rows. Optional because
     * not every entry point has them loaded; without them the rules INFER the shares, which cannot answer a span a
     * Units edit stretched (see duration.storedSharesById).
     */
    assignmentSharesLoader?: (taskId: string) => Dictionary<number> | undefined
) => (entity: ITask, rawChanges: Dictionary<unknown>, edits?: PanelEdits): Dictionary<unknown> => {
    const changes = rawChanges as Partial<ITaskAttrs>;
    // Bug 17384 – the shares in play, which the REPLAY moves forward: a step that settles new ones (an add derives
    // the newcomer's) hands them to the next step, because no stored row holds them yet.
    let storedShares = assignmentSharesLoader?.(entity.id);
    const SCHEDULING: (keyof ITaskAttrs)[] = ["StartDate", "DueDate", "Duration", "AssignedTo", "Effort", "Predecessor"];
    // Only an AUTO LEAF task derives its schedule — the Effort/Duration/Units rules are what "auto" means. A MANUAL
    // task has none of those (its Effort is whatever the user typed), but its Start/Due/Duration still reconcile
    // against each other exactly as the server reconciles them for any task, so it gets the date replay and nothing
    // else. That replay is the FULL one, not just the three date fields: the engine reconciles a manual task's dates
    // around a PREDECESSOR and around an ASSIGNEE change too, neither being gated on auto mode. What it does NOT get
    // is the Effort/Duration/Units rules, so its Effort never moves and per-assignment Units are kept exactly as
    // sent. The one task that reconciles NOTHING is an AUTO SUMMARY, whose dates roll up from its children.
    const derivesSchedule = entity.isAutoMode && !entity.hierarchy.isParent;
    const reconcilesDatesOnly = !derivesSchedule && !entity.isAutoMode;
    if ((!derivesSchedule && !reconcilesDatesOnly) || !SCHEDULING.some(k => k in changes)) {
        return {};
    }
    const isEffortProject = project.settings.progressCalculationType === ProgressCalculationType.Effort;
    const taskMap = toDictionaryById(tasksLoader() ?? []);

    // Bug 17138 – LAST CHANGE WINS is a REPLAY, not a choice of one trigger out of `changes`. The scheduling rules are
    // not commutative, so the same payload from two different histories must produce two different tasks:
    //   • "Duration 5, then assign"  – the assignment is applied to a 5-day span, so the first-assignment rule decides:
    //     a share that fits derives sub-100% Units and keeps 5d; one that does not keeps 100% and extends (120h ⇒ 15d).
    //   • "assign, then Duration 5"  – the span edit is last, so it wins and Effort recomputes from it × Units (40h).
    // Picking a single trigger (the latest edit) and applying it to the accumulated `changes` cannot express both, and
    // deriving from the payload alone cannot either. So apply every edit IN ORDER, each to the result of the one
    // before it — exactly as if the panel had saved after every change — and let the last one land last.
    const RECONCILED_DATES = new Set<keyof ITaskAttrs>(["StartDate", "DueDate", "Duration"]);
    const RECONCILED_DATE_KEYS: (keyof ITaskAttrs)[] = ["StartDate", "DueDate", "Duration"];
    // Non-scheduling edits are context for the rules (Completed Work, Progress, …), so seed them once up front. The
    // scheduling ones are introduced by the replay: Start/Due/Duration must reach handleDuration as an UPDATE against
    // a CONSISTENT pre-change triple, and merging a raw new value first (a Start moved past the old Due) collapses its
    // resource-calendar span precompute to a 0/negative span and sets Duration to 0 — a MOVE behaving like a resize.
    let attrs: ITaskAttrs = { ...entity.attributes } as ITaskAttrs;
    (Object.keys(changes) as (keyof ITaskAttrs)[])
        .filter(k => !RECONCILED_DATES.has(k) && !(SCHEDULING as string[]).includes(k))
        .forEach(k => { (attrs as unknown as Dictionary<unknown>)[k] = (changes as Dictionary<unknown>)[k]; });

    const stateOf = (): ITask => ({ ...entity, attributes: attrs });
    const dictated = (): DictatedDates => TaskScheduleCalculator.GetDictatedDates(
        attrs.Predecessor, taskMap, calendar,
        getAssigneeCalendars(useResourceCalendar, attrs.AssignedTo, calendar, resources, calendarsById),
        shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay, stateOf());

    // One replay step: the field the user edited and the value they left in it. An AssignedTo edit also says whether it
    // was a per-assignment UNITS change rather than a membership one — the two follow different rules.
    type Step = { key: keyof ITaskAttrs, value: unknown, unitsOnly?: boolean };

    // `edits.order` records each field ONCE, at the position of its last edit, which is all last-change-wins needs —
    // with one exception. Adding an assignee and then editing its Units are two edits of the SAME field, so they
    // collapse into a single AssignedTo entry carrying the final Units, and replaying that as one step would hand the
    // typed Units to the first-assignment rule, which DERIVES Units and threw them away (a typed 50% snapped back to
    // 100% and the Duration never moved). Split it back into the two edits it stands for: the membership add at the
    // default Units, then the Units edit on top.
    const buildSteps = (): Step[] => {
        const ordered = (edits?.order ?? []).filter(k => (SCHEDULING as string[]).includes(k) && k in changes) as (keyof ITaskAttrs)[];
        // No edit order (a caller without panel tracking): fall back to a fixed order — dates, then assignees, then
        // Effort — which is the sequence a user physically follows on the panel.
        const keys = ordered.length
            ? ordered
            : (["Predecessor", "StartDate", "Duration", "DueDate", "AssignedTo", "Effort"] as (keyof ITaskAttrs)[]).filter(k => k in changes);
        return keys.flatMap<Step>(key => {
            if (key !== "AssignedTo") {
                return [{ key, value: (changes as Dictionary<unknown>)[key] }];
            }
            const assignees = (changes.AssignedTo ?? []) as IUserInfo[];
            const membershipChanged = !sameAssigneeIds(assignees, entity.attributes.AssignedTo);
            if (!membershipChanged) {
                // Same members: a pure Units edit (`edits.unitsOnly` confirms it for the panel, which knows the set the
                // user started FROM — on create the entity has no assignees at all, so the id comparison cannot).
                return [{ key, value: assignees, unitsOnly: edits ? !!edits.unitsOnly : true }];
            }
            if (!edits?.unitsOnly) {
                return [{ key, value: assignees }];
            }
            // Membership changed AND the last AssignedTo edit was a Units change: replay both.
            return [
                { key, value: asStoredUnits(assignees, entity.attributes.AssignedTo) },
                { key, value: assignees, unitsOnly: true }
            ];
        });
    };

    for (const step of buildSteps()) {
        // The task as it would have been STORED before this edit — what each rule reads its "current" values from
        // (`hadPriorAssignees`, the Effort to preserve, the span to measure against). This is what makes the replay
        // equivalent to saving after every change: by the time a later Duration edit runs, the assignee it must
        // recompute Effort for is already on the task, so it takes the span-edit path instead of the first-assignment one.
        const before = { ...entity, attributes: attrs };
        const primary = { [step.key]: step.value } as Partial<ITaskAttrs>;

        // 1. Reconcile Start/Due/Duration around this edit.
        if (step.key === "Predecessor") {
            attrs = { ...attrs, Predecessor: step.value as ITaskAttrs["Predecessor"], ...applyPredecessors({ ...entity, attributes: { ...attrs, Predecessor: step.value as ITaskAttrs["Predecessor"] } }, step.value as ITaskAttrs["Predecessor"], taskMap, calendar, resources, calendarsById, useResourceCalendar, shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay) };
        } else if (step.key === "Effort") {
            attrs = { ...attrs, Effort: step.value as ITaskAttrs["Effort"] };
            // Setting an Effort is what switches the reprojection ON for a Flexible task, so the span moves with it
            // (Bug 17141): the engine narrows Duration to what the assignees really share inside the span and then
            // gives each of them that many of their OWN days. Only the Units used to change here. A MANUAL task gets
            // the same effect from the row-walk mirror further down, which this step now feeds an Effort to.
            const flexEffortSpan = previewFlexibleEffortEdit({ ...entity, attributes: attrs }, calendar, resources, calendarsById, useResourceCalendar);
            if (flexEffortSpan) {
                attrs = { ...attrs, ...flexEffortSpan };
            }
        } else {
            // A Due edit is a RESIZE (Duration re-derives); everything else preserves Duration.
            attrs = { ...attrs, ...primary, ...handleDuration(attrs, primary, calendar, resources, calendarsById, useResourceCalendar, step.key !== "DueDate", dictated(), reprojectsOntoAssigneeDays({ ...entity, attributes: attrs })) };
        }

        // A membership-only assignee change also NARROWS the Duration to the days the new set actually has inside the
        // stored span, as the server's AdjustScheduleDataUpdates does via GetRealDuration before re-seating the
        // dates. handleDuration performs the same narrowing for both task types (Bug 17140), so this runs on a
        // different BASIS rather than as the only place it happens: handleDuration measures the span the step came
        // from, this one the span reconciliation left behind.
        //
        // Same branch condition as the server's — an assignee change with no Start/Due/Duration alongside it — and
        // only when the STORED schedule is not valid for the new set (IsStoredScheduleRejectedFor). That single test
        // covers all three membership changes, which neither half of it does alone: narrowing on every ADD is wrong
        // (the engine EXTENDS instead), and narrowing only on a FIRST assignment misses REMOVALS, where it is the
        // whole answer.
        if (reconcilesDatesOnly && step.key === "AssignedTo" && useResourceCalendar
            && !RECONCILED_DATE_KEYS.some(k => k in changes)
            && storedScheduleRejectedFor(before.attributes, getAssigneeCalendars(useResourceCalendar, attrs.AssignedTo, calendar, resources, calendarsById), calendar)) {
            const storedStart = toUniversalDateTime(before.attributes.StartDate);
            const storedDue = toUniversalDateTime(before.attributes.DueDate);
            const newCalendars = getAssigneeCalendars(useResourceCalendar, attrs.AssignedTo, calendar, resources, calendarsById);
            // Three payloads do NOT narrow, mirroring handleDuration's AssignedTo branch:
            //  • Task 17151 – a full unassignment. The narrowing is a per-ASSIGNEE minimum and getAssigneeCalendars
            //    stands the GLOBAL calendar in for an empty set, so it would measure the stored span on the PROJECT
            //    calendar and cut a Duration the departed resource's own week had made longer. With no assignees
            //    there is no minimum to take — the server's GetRealDuration fallback.
            //  • Bug 17153 – a FIRST assignment: with nobody having left, the stored span's days are not a departed
            //    resource's, so its Duration is already the real one.
            //  • Bug 17156 – measured over the span's OWNERS (the new set plus whoever this step removes), because
            //    the MIN is the real duration only while that set is whole. Only the day count comes from the wider
            //    set; the Due below still walks the NEW calendars.
            const real = storedStart && storedDue && before.attributes.Duration != null
                && (attrs.AssignedTo?.length ?? 0) > 0 && (before.attributes.AssignedTo?.length ?? 0) > 0
                ? getRealDuration(storedStart, storedDue, before.attributes.Duration,
                    getAssigneeCalendars(useResourceCalendar, getSpanOwners(before.attributes.AssignedTo, attrs.AssignedTo), calendar, resources, calendarsById), calendar)
                : undefined;
            const start = toUniversalDateTime(attrs.StartDate);
            if (real != null && start && attrs.Duration != null && real < attrs.Duration) {
                const due = calculateDueDate(start, real, calendar, newCalendars);
                attrs = { ...attrs, Duration: real, DueDate: due.toUniversalIsoDateTimeString() };
            }
        }

        // Mirror SyncTotalsAndExtendSpanForReprojection for a MANUAL task. Its effort rows are an EVEN split over
        // ceil(Duration) days of each assignee's OWN calendar, starting at the Start or — when they do not work it —
        // their next working day, and the span then EXTENDS to the latest row. So the span follows the assignees, and
        // the combined-calendar walk handleDuration does is not enough. That holds for an assignee change AND a span
        // edit, so it runs after every step. Extend only, matching the engine's EffortMaxDate > DueDate guard. An
        // AUTO task gets the same effect from applyFixedUnitsRecalc, hence manual-only.
        //
        // Gated on an Effort being SET (Bug 17141): with none the engine never reprojects at all — RecalculateEffort's
        // UC2 guard gives each assignee null-effort rows over the task's OWN span instead of walking their calendars,
        // so there is no row to extend or contract to. Task type does not enter into it, because Fixed Units'
        // derive-Effort rules are auto-only.
        if (reconcilesDatesOnly && useResourceCalendar && (attrs.AssignedTo?.length ?? 0) > 0 && attrs.Effort != null) {
            const spanStart = toUniversalDateTime(attrs.StartDate);
            const spanDue = toUniversalDateTime(attrs.DueDate);
            // The row count is ceil() of the Duration the ROWS are built from, which is the one this step REQUESTED —
            // not the one reconciliation left behind. They differ, and using the latter double-extends: handleDuration
            // declines to shrink a span it cannot measure at the new value (Duration 3 → 2 over a Mon–Wed + Wed–Fri pair
            // comes back 3d/Thu, which is already what the engine stores), and re-walking THREE days from there ran to
            // the following Monday. The engine walks the requested 2 and then extends, landing on the same 3d.
            const requested = step.key === "Duration" ? (step.value as number | null | undefined) : undefined;
            // Extend OR contract, the engine's two guards: lastWorkedDate.Date > DueDate extends; with
            // contractToEffort it also pulls the Due BACK when the rows end earlier (an assignee removal leaves
            // trailing empty days). Contraction is off whenever the caller asked for a Due/Duration or a
            // predecessor dictates one — the same `contractSpanToEffort` the engine computes.
            const mayContract = !RECONCILED_DATE_KEYS.some(k => k === "DueDate" || k === "Duration" ? k in changes : false)
                && !dictated().dueDate;
            const reconciledSpan = spanStart && spanDue
                ? extendSpanToAssigneeRows(attrs, requested, spanStart, spanDue, mayContract, { calendar, resources, calendarsById, useResourceCalendar, storedShares })
                : undefined;
            if (reconciledSpan) {
                attrs = { ...attrs, ...reconciledSpan };
            }
        }

        if (!derivesSchedule || !isEffortProject) {
            continue;
        }

        // 2. Apply this edit's Effort/Duration/Units rule. `spanEdited` is left to default: it means "did THIS edit set
        // the span", which for a single-key step is exactly what applyFixedUnitsRecalc already infers from `primary`.
        // `mayContract` is the engine's contractSpanToEffort, and the same one the manual row walk above computes: a
        // span re-derived from the effort rows may end EARLIER than the stored one only while nothing else claims the
        // Due. A single-key step carries no Due/Duration of its own, so only a dictated Due can forbid it.
        // `effortStated` is the engine's own question about the SAVE PAYLOAD (Bug 17180): a 0%-Units edit that comes
        // with an Effort does not get the 0%-Units redistribution — the server leaves that combination to the Effort
        // rules, which preserve the stated Effort whole (see ApplyUnitsBasedSpan's effortStated bail). It cannot be
        // read off `primary`/`attrs`: the replay seeds `attrs` from the stored task, so every step sees an Effort.
        const sharesUsed: { value?: Dictionary<number> } = {};
        const fu = step.key === "Effort"
            ? undefined
            : applyFixedUnitsRecalc(project, before, primary, attrs, { calendar, resources, calendarsById, useResourceCalendar, storedShares },
                {
                    sharesUsed,
                    unitsEdit: step.unitsOnly, mayContract: !dictated().dueDate,
                    effortStated: nameof<ITaskAttrs>("Effort") in changes,
                    // Read from the STORED task, not from `before`: the replay splits an add-then-Units edit into two
                    // steps, so by the second one `before` already carries the assignees step 1 added — while the
                    // server, which sees one payload, still finds no persisted rows for either.
                    firstAssignment: (entity.attributes.AssignedTo?.length ?? 0) === 0
                });
        if (fu) {
            attrs = { ...attrs, ...fu };
        }
        if (sharesUsed.value) {
            storedShares = sharesUsed.value;
        }

        // 3. FIXED UNITS: an Effort edit keeps the Effort and re-derives the span from each assignee's Units×capacity
        // (applyFixedUnitsRecalc deliberately does not trigger on Effort — see its header).
        if (entity.taskType === TaskType.FixedUnits && step.key === "Effort") {
            const effortPreview = previewFixedUnitsEffortEdit(project, stateOf(), attrs.Effort ?? undefined, calendar, resources, calendarsById, useResourceCalendar);
            if (effortPreview) {
                attrs = { ...attrs, ...effortPreview };
            }
        }

        // Bug 17223 – every span derived above is anchored at the START, which a to-Finish predecessor's dictated Due
        // overrules: re-anchor on that Due and take the difference off the Start. Placed exactly where the engine
        // places it — after both re-derivations and BEFORE the Flexible Units are derived, so those measure the
        // pinned span rather than the one it replaced.
        const pinned = pinSpanToDictatedDue(attrs, dictated().dueDate,
            calendar, getAssigneeCalendars(useResourceCalendar, attrs.AssignedTo, calendar, resources, calendarsById));
        if (pinned) {
            attrs = { ...attrs, ...pinned };
        }

        // FLEXIBLE: Units are always DERIVED, so re-derive them after every step from the Effort/Duration this step
        // produced. A user Units edit is authoritative and is left exactly as typed.
        const startDate = toUniversalDateTime(attrs.StartDate);
        if (entity.taskType !== TaskType.FixedUnits && startDate
            && attrs.Duration != null && attrs.Duration > 0 && (attrs.Effort ?? 0) > 0 && (attrs.AssignedTo?.length ?? 0) > 0
            && (!step.unitsOnly || redistributesFromZeroUnits(attrs.AssignedTo))) {
            const derived = deriveFlexibleUnits(attrs.AssignedTo!, attrs.Effort!, attrs.Duration, startDate, toUniversalDateTime(attrs.DueDate),
                { calendar, resources, calendarsById, useResourceCalendar, storedShares });
            attrs = { ...attrs, AssignedTo: keepUnitsTheDerivationCannotSpeakFor(attrs.AssignedTo!, derived) };
        }
    }

    // Return only the derived scheduling overlay (a mutually consistent Start/Due/Duration/Units/Effort set). The
    // overlay OVERRIDES `changes` in the panel's display, so it must cover only what this run actually derived: for a
    // manual task that is the reconciled dates alone — including Effort/AssignedTo would hand back the STORED values
    // and silently revert the user's own edit to them.
    const finalAttrs = attrs as unknown as Dictionary<unknown>;
    const overlay: Dictionary<unknown> = {};
    ((derivesSchedule
        ? ["StartDate", "DueDate", "Duration", "AssignedTo", "Effort", "RemainingWork"]
        : ["StartDate", "DueDate", "Duration"]) as (keyof ITaskAttrs)[])
        .forEach(k => {
            if (finalAttrs[k] !== undefined) {
                overlay[k] = finalAttrs[k];
            }
        });
    return overlay;
};

// The scheduling keys that compete with an assignment for the EDIT ORDER.
const SCHEDULE_EDIT_KEYS: (keyof ITaskAttrs)[] = ["StartDate", "DueDate", "Duration", "Effort", "Predecessor"];

// What of the panel's replay this session cannot state as raw edits. `changes` and `edits` alone decide it — no
// calendars, no derived values — so it reads as the question it is: which session is this?
//
// Bug 17138 – everywhere else the raw edits are enough: a single scheduling field identifies its own rule and the
// server derives the rest authoritatively, which is one less client mirror of a scheduling rule. But the SERVER SEES
// NO ORDER, and the rules are not commutative — `{AssignedTo, Duration 5}` is ambiguous between "type 5d, then
// assign" (the first-assignment rule decides on that 5-day span: a share that fits derives sub-100% Units, one that
// does not extends the span) and "assign, then type 5d" (the span wins, Effort recomputes from it).
//
// The SHAPES below are pinned by savePayloadReplay.test.ts and their NUMBERS by the shared scenario contract, both
// keyed by bug id — the evidence for each answer lives there rather than here.
type ReplayedPayload =
    // Raw `changes`. The server's own rules resolve it, and the assignee-change repair in AdjustScheduleDataUpdates
    // depends on the payload staying span-FREE.
    | "none"
    // The replayed per-assignment Units, which state the ORDER without stating a span. A first assignment never
    // needs one: the contract's REPLAY-*-first-assign-* pairs land on the same numbers with a span and without.
    | "units"
    | "unitsAndSpan"
    | "unitsAndEffort";

const replayNeededFor = (entity: ITask, changes: Dictionary<unknown>, edits?: PanelEdits): ReplayedPayload => {
    const assignees = (changes as Partial<ITaskAttrs>).AssignedTo;
    const priorAssignees = entity.attributes.AssignedTo ?? [];
    const isFixedUnits = entity.taskType === TaskType.FixedUnits;
    const competingScheduleEdit = SCHEDULE_EDIT_KEYS.some(k => k in changes);
    // A span the user typed THEMSELVES is unambiguous already, and replacing it with the reconciled one would
    // re-extend an already-extended span.
    const spanTypedInThisSession = nameof<ITaskAttrs>("Duration") in changes
        || nameof<ITaskAttrs>("DueDate") in changes;
    // `edits.unitsOnly` rather than a Units comparison: the ids alone cannot tell a typed Units from a derived one
    // (on Flexible the panel DISPLAYS derived Units, so "different Units" means nothing). It is the same signal the
    // replay splits `changes.AssignedTo` back into two steps with (see buildSteps).
    const unitsTypedInThisSession = !!edits?.unitsOnly;

    if ((assignees?.length ?? 0) > 0 && priorAssignees.length === 0) {
        // A FIRST assignment. On its own it is unambiguous — the engine derives the Units when none are stated
        // (UC3/UC4) and honors them when they are (TryApplyUnitsEditRules' firstAssignmentWithStatedUnits), deriving
        // the span either way. Only another edit beside it makes the order matter. Fixed Units needs the replay for
        // any such edit, because that is where the first-assignment rule reads the span to decide (UC3); Flexible
        // only when the user also typed the Units, which is then the whole statement of the edit.
        return competingScheduleEdit && (isFixedUnits || (unitsTypedInThisSession && !spanTypedInThisSession))
            ? "units"
            : "none";
    }

    // A MEMBERSHIP change with per-assignment Units typed in the same session — the only sessions left that replay
    // anything. TryApplyUnitsEditRules keys off the PERSISTED assignee set, so `{AssignedTo}` alone reads as a plain
    // add/remove and DERIVES the Units instead of honouring what was typed. WHICH value states the session differs by
    // shape, and a span is the wrong one for three of the four:
    //
    //  • FLEXIBLE add — nothing replays: the engine reads the typed Units itself (addWithStatedUnits). A span would
    //    be re-read as a count of each assignee's OWN days and re-extend the task (Bug 17159).
    //  • FIXED add — the EFFORT. The add settles a new pool the engine cannot recover, because Fixed Units derives
    //    Effort = Duration × ΣUnits × capacity where Flexible would have preserved it; the span is left to the Units
    //    rules, and sending one instead spends its union-day count as own days (Bug 17368).
    //  • FIXED removal — nothing replays: the engine resolves it (removalKeepingShares), and a span would
    //    DISQUALIFY those rules, a stated Duration meaning recompute-Effort. Harmless while one survivor takes the
    //    whole pool, wrong the moment two hold unequal shares (Bugs 17375/17376).
    //  • FIXED swap — the EFFORT again (Bug 17379). It is order-dependent the same way an add is: the swap settles a
    //    new pool (the departing share out, a recomputed one for the newcomer in) and the Units then re-span it, and
    //    only the pool states that. Never a SPAN (Bug 17377), a stated Duration meaning recompute-Effort here too.
    //  • FLEXIBLE removal or swap — the span still carries it. Span-free the assignee-change repair re-derives the
    //    Units from the days it kept and the typed value is lost.
    const isAddToExistingAssignees = priorAssignees.length > 0
        && (assignees?.length ?? 0) > priorAssignees.length
        && priorAssignees.every(p => (assignees ?? []).some(a => a.id === p.id));
    if (!unitsTypedInThisSession
        || sameAssigneeIds(assignees, entity.attributes.AssignedTo)
        || spanTypedInThisSession) {
        return "none";
    }
    if (isAddToExistingAssignees) {
        return isFixedUnits ? "unitsAndEffort" : "none";
    }
    const isSwap = (assignees ?? []).some(a => !priorAssignees.some(p => p.id === a.id))
        && priorAssignees.some(p => !(assignees ?? []).some(a => a.id === p.id));
    if (isFixedUnits) {
        return isSwap ? "unitsAndEffort" : "none";
    }
    return "unitsAndSpan";
};

// Bug 17138 – a replayed SPAN may only travel while the engine does not still have to RE-SEAT the stored one for the
// new assignee set. AdjustScheduleDataUpdates repairs that only for a span-FREE assignee payload, so a span alongside
// one leaves the Start on a day nobody works and EnsureSchedulingRulesMet rejects the whole save. Asking the engine's
// own question (IsStoredScheduleRejectedFor, mirrored by storedScheduleRejectedFor) hands those sessions back to the
// repair, which derives what the panel previewed anyway; what is given up is only the Units typed in the same
// session, and that beats a silent no-op.
const replayedPayloadCanTravel = (
    entity: ITask,
    assignees: IUserInfo[] | undefined,
    newCalendars: CalendarDataSet[],
    calendar: CalendarDataSet,
    derived: Dictionary<unknown>,
    derivedDuration: number | undefined
): boolean => {
    const isRemovalOnly = (assignees ?? []).every(a => (entity.attributes.AssignedTo ?? []).some(p => p.id === a.id));
    if (!isRemovalOnly) {
        return !storedScheduleRejectedFor(entity.attributes, newCalendars, calendar);
    }
    // Bug 17159 – too broad for a REMOVAL, which answers YES to nearly all of them: the stored span was measured on a
    // union that INCLUDED the departing resource, so it rarely still measures the Duration on the survivors alone.
    // Only the START half is unrecoverable — a mismatching span is repaired by the Duration this payload carries.
    if (startNotWorkedBy(entity.attributes, newCalendars)) {
        return false;
    }
    // Fixed Units no longer sends a span for a removal at all (see replayNeededFor), so this is the Flexible gate;
    // the task-type clause is kept as a guard rather than an assumption about who reaches it.
    if (entity.taskType === TaskType.FixedUnits || derivedDuration == null) {
        return true;
    }
    // Bug 17159 – a FLEXIBLE span travels only when the engine reads it back as the SAME span: Duration is a union-day
    // count on the way in and a per-assignee OWN-day count on the way out, so it survives the round trip only while
    // every survivor has that many of their own days inside it. When they do not, nothing in the payload can state
    // that span — fall back to the raw edit and let the repair derive it.
    const spanStart = toUniversalDateTime((derived[nameof<ITaskAttrs>("StartDate")] ?? entity.attributes.StartDate) as string | undefined);
    const spanDue = toUniversalDateTime(derived[nameof<ITaskAttrs>("DueDate")] as string | undefined);
    const perAssigneeDays = spanStart && spanDue
        ? getRealDuration(spanStart, spanDue, derivedDuration, newCalendars, calendar)
        : undefined;
    return !(perAssigneeDays == null || Math.abs(perAssigneeDays - derivedDuration) > DURATION_COMPARISON_EPSILON);
};

// The panel's save payload. The user's raw `changes` by default — the server derives the schedule authoritatively and
// owns the dates — replaced by the panel's replayed result (buildDeriveDisplayAttributes) for the sessions the raw
// edits cannot describe, which `replayNeededFor` names. Effort travels in exactly one of them (a Fixed Units add made
// alongside a Units edit, where the pool the add settled is what the engine cannot recover); everywhere else the
// engine's recompute reproduces it and sending it would be overwritten anyway.
export const buildTaskSavePayload = (
    project: ProjectInfo,
    calendar: CalendarDataSet,
    resources: Dictionary<Resource>,
    calendarsById: Dictionary<CalendarDataSet>,
    useResourceCalendar: boolean
) => (entity: ITask, changes: Dictionary<unknown>, derived: Dictionary<unknown>, edits?: PanelEdits): Dictionary<unknown> => {
    if (!(nameof<ITaskAttrs>("AssignedTo") in changes)
        || !entity.isAutoMode
        || entity.hierarchy.isParent
        || project.settings.progressCalculationType !== ProgressCalculationType.Effort) {
        return changes;
    }
    const replay = replayNeededFor(entity, changes, edits);
    if (replay === "none") {
        return changes;
    }
    const assignees = (changes as Partial<ITaskAttrs>).AssignedTo;
    const newCalendars = getAssigneeCalendars(useResourceCalendar, assignees, calendar, resources, calendarsById);
    const derivedDuration = derived[nameof<ITaskAttrs>("Duration")] as number | undefined;
    // The travel guard asks whether the engine's assignee-change repair is still available, and what takes it away
    // is a Start/Due/Duration in the payload — AdjustScheduleDataUpdates' repair branch fires only on an update
    // that carries NONE of them. An Effort does not, so a pool-carrying payload is asked the question only when the
    // user's own edits bring a date along.
    const carriesDate = replay !== "unitsAndEffort"
        || (["StartDate", "DueDate", "Duration"] as (keyof ITaskAttrs)[]).some(k => k in changes);
    if (carriesDate && !replayedPayloadCanTravel(entity, assignees, newCalendars, calendar, derived, derivedDuration)) {
        return changes;
    }
    const derivedEffort = derived[nameof<ITaskAttrs>("Effort")] as number | undefined;
    const derivedAssignees = derived[nameof<ITaskAttrs>("AssignedTo")];
    return {
        ...changes,
        ...(replay === "unitsAndSpan" && derivedDuration != null
            ? { [nameof<ITaskAttrs>("Duration")]: derivedDuration }
            : {}),
        ...(replay === "unitsAndEffort" && derivedEffort != null
            ? { [nameof<ITaskAttrs>("Effort")]: derivedEffort }
            : {}),
        ...(Array.isArray(derivedAssignees) && derivedAssignees.length
            ? { [nameof<ITaskAttrs>("AssignedTo")]: derivedAssignees }
            : {}),
    };
};

export const inlineRendersBuilder = (
    project: ProjectInfo,
    groups: Metadata.Group[],
    calendar: CalendarDataSet,
    resources: Dictionary<Resource>,
    calendarsById: Dictionary<CalendarDataSet>,
    useResourceCalendar: boolean,
    urlBuilder: (task: { id: string }) => string | undefined,
    tasksLoader: () => LoadedTask[] | undefined,
    shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay: boolean = true
) => ({
    [nameof<ITaskAttrs>("AssignedTo")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: IValidator): JSX.Element | null =>    {
        return <AssigneePickerInput {...props}
            searchUrl={`api/${searchPathMap[field.type as keyof typeof searchPathMap]}/find`}
            multichoice={field.settings?.multichoice}
            validator={validator}
            onEditComplete={() => {}}/>
    },
    [nameof<ITaskAttrs>("StartDate")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: Validator): JSX.Element | null => {
        const taskMap = toDictionaryById(tasksLoader() ?? []);
        const assigneeCalendars = getAssigneeCalendars(useResourceCalendar, state.attributes.AssignedTo, calendar, resources, calendarsById);
        const dictatedDates = TaskScheduleCalculator.GetDictatedDates(state.attributes.Predecessor, taskMap, calendar, assigneeCalendars, shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay, state);
        const minStartDate = isTaskPredecessorHierarchyValid(state, taskMap) ? dictatedDates?.startDate : undefined;
        return <TaskStartDueDatePickerInput {...props}
            assigneeCalendars={assigneeCalendars}
            useResourceCalendar={useResourceCalendar}
            inputProps={{ readOnly: field.isReadonly }}
            validator={validator}
            showTime
            minDate={minStartDate}
            onChanged={(value) => {
                // Send only the (day-start stamped) Start — the same minimal payload the edit panel sends; the server
                // derives Duration/Due from it. A Start move never defines a span, and bundling the client-derived
                // Duration/Due made it read as one: on Fixed Units that recomputes Effort instead of preserving it
                // (Bug 17036), and on either type a requested span suppresses the contraction that keeps a stretched
                // span from ratcheting up on every move. The stamping is what keeps the date itself workable — the
                // picker only OFFERS days the assignees work, and a TYPED one is snapped onto the next of them.
                props.onChanged?.(stampStart(value, assigneeCalendars, calendar, useResourceCalendar));
            }}
            onEditComplete={() => props.onEditComplete(null)}
        />;
    },
    [nameof<ITaskAttrs>("DueDate")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: IValidator): JSX.Element | null => {
        const taskMap = toDictionaryById(tasksLoader() ?? []);
        const assigneeCalendars = getAssigneeCalendars(useResourceCalendar, state.attributes.AssignedTo, calendar, resources, calendarsById);
        const dictatedDates = TaskScheduleCalculator.GetDictatedDates(state.attributes.Predecessor, taskMap, calendar, assigneeCalendars, shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay, state);
        const minDueDate = utils.max([isTaskPredecessorHierarchyValid(state, taskMap) ? dictatedDates?.dueDate : undefined, state.attributes.StartDate]);
        return <TaskStartDueDatePickerInput {...props}
            assigneeCalendars={assigneeCalendars}
            useResourceCalendar={useResourceCalendar}
            inputProps={{ readOnly: field.isReadonly }}
            validator={validator}
            showTime
            minDate={minDueDate}
            onChanged={(value) => {
                const stamped = stampDue(value, assigneeCalendars, calendar, state.attributes.Duration, state.attributes.StartDate);
                props.onChanged?.(stamped, handleDuration(state.attributes, { DueDate: stamped }, calendar, resources, calendarsById, useResourceCalendar, false, dictatedDates));
            }}
            onEditComplete={() => props.onEditComplete(null)}
        />;
    },
    [nameof<ITaskAttrs>("Duration")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: IValidator): JSX.Element | null => {
        return <NumberInput {...props} inputProps={toNumberInputProps(field)} validator={validator}
            onChanged={(value) => {
                // Bug 17128 – send only the raw Duration, exactly as the edit panel does; the server derives Due/Effort/
                // Units from it. The derived Start/Due/Duration this used to bundle were actively harmful: `Duration`
                // counts the assignee UNION's working days but the engine spends a requested Duration as a count of each
                // assignee's OWN days, so handleDuration re-measured the typed value into a different number — and since
                // ViewService.buildUpdates lets the field value win, the payload became an INCONSISTENT triple (the
                // derived Due 08-10 labelled with the typed 3). The server then re-derived the Due from Start+3 and
                // collapsed the edit back to where it started. With the Duration alone there is nothing to reconcile.
                props.onChanged?.(value);
            }}
            onEditComplete={() => props.onEditComplete(null)}
        />;
    },
    [nameof<ITaskAttrs>("Progress")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: IValidator): JSX.Element | null => {
        return <NumberInput {...props} inputProps={toNumberInputProps(field)} validator={validator} format={field.settings?.format}
            onChanged={(value) => props.onChanged?.(value, validator?.isValid(value) ? TaskProgressCalculator.RecalculateByProgress(project, state, value) : undefined)}
            onEditComplete={() => props.onEditComplete(null)}
        />;
    },
    [nameof<ITaskAttrs>("Effort")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: IValidator): JSX.Element | null => {
        return <NumberInput {...props} inputProps={toNumberInputProps(field)} validator={validator} format={field.settings?.format}
            onChanged={(value) => props.onChanged?.(value, validator?.isValid(value) ? TaskProgressCalculator.RecalculateByEffort(project, state, value) : undefined)}
            onEditComplete={() => props.onEditComplete(null)}
        />;
    },
    [nameof<ITaskAttrs>("CompletedWork")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: IValidator): JSX.Element | null => {
        return <NumberInput {...props} inputProps={toNumberInputProps(field)} validator={validator} format={field.settings?.format}
            onChanged={(value) => props.onChanged?.(value, validator?.isValid(value) ? TaskProgressCalculator.RecalculateByCompletedWork(project, state, value) : undefined)}
            onEditComplete={() => props.onEditComplete(null)}
        />;
    },
    [nameof<ITaskAttrs>("RemainingWork")]: (props: IInputProps, state: ITask, field: Metadata.Field, validator?: IValidator): JSX.Element | null => {
        return <NumberInput {...props} inputProps={toNumberInputProps(field)} validator={validator} format={field.settings?.format}
            onChanged={(value) => props.onChanged?.(value, validator?.isValid(value) ? TaskProgressCalculator.RecalculateByRemainingWork(project, state, value) : undefined)}
            onEditComplete={() => props.onEditComplete(null)}
        />;
    },
    [nameof<ITaskAttrs>("Group")]: (props: IInputProps, state: any, field: Metadata.Field): JSX.Element | null =>
        <GroupDropdown value={props.value} groups={groups} hideCaretDown
            dropdownInputProps={{ ...props, onEditComplete: () => props.onEditComplete(null) }} />,
    [nameof<ITaskAttrs>("Predecessor")]: (props: IInputProps, state: ITask, field: Metadata.Field): JSX.Element | null => null
})

export const toNumberInputProps = (field: Metadata.Field): Dictionary<any> => {
    return {
        readOnly: field.isReadonly,
        disabled: field.isReadonly,
        placeholder: field.settings?.placeholder
    };
}

export class DictatedDates {
    startDate?: Date;
    dueDate?: Date;
}

// US 16555: dictates a successor date from a predecessor anchor instant and a decimal working-day lag, as a
// single proportional working-day walk (forward for positive lag, backward for negative). A dictated START is
// snapped to the next working instant so it never lands inside a break or non-working day (e.g. lag 1.25 from
// a 0.75-of-day finish completes that day plus a full next day, landing at the start of the day after); a
// dictated FINISH keeps the exact end-of-work instant. Mirrors TaskWorkloadHandler.Dictate; inverse of GetLag.
// The LAG WALK is measured on the GLOBAL (project) calendar, never on the successor's assignee calendars: a lag
// belongs to the DEPENDENCY, so "+2d" means the same two project working days whoever is assigned, and it stays
// the inverse of GetLag (which the lag editor computes on the project calendar alone). Only the final snap of a
// dictated START uses `calendars` — the Start must land on a day the successor's own assignees work.
const dictate = (anchor: Date | undefined, lag: number | undefined, isStart: boolean, keepAtRangeEnd: boolean,
    calendars: CalendarDataSet[], globalCalendar: CalendarDataSet): Date | undefined => {
    if (!anchor) {
        return undefined;
    }
    const l = lag ?? 0;
    const lagCalendars = [globalCalendar];
    const shifted = l === 0 ? anchor
        : l > 0 ? utils.addDuration(anchor, l, lagCalendars, globalCalendar)
            : utils.subtractDuration(anchor, -l, lagCalendars, globalCalendar);
    // Task 13311: keepAtRangeEnd keeps a dictated START that lands on a working-range boundary there (zero-duration
    // task in keep mode) instead of snapping forward to the next working instant.
    return isStart ? utils.nextWorkingInstant(shifted, calendars, globalCalendar, keepAtRangeEnd) : shifted;
};

// this functionality realized on server side too (in TaskScheduleCalculator)
export class TaskScheduleCalculator {
    public static GetDatesDictatedByPredecessor(
        predecessor: LoadedTask | undefined,
        info: IPredecessorInfo,
        globalCalendar: CalendarDataSet,
        calendarSettings: CalendarDataSet[],
        shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay: boolean = true,
        currentTask?: LoadedTask): DictatedDates {
        // US 16555: every dependency type is two choices — which predecessor endpoint is the anchor (its finish
        // for FS/FF, its start for SS/SF) and which successor endpoint we dictate (a Start, snapped forward off
        // breaks/non-working days; a Finish, not). Both collapse into one dictate() call. Trust the time-of-day
        // on the predecessor's Start/Due (kept normalized in storage). Mirrors the backend.
        const predecessorStart = toUniversalDateTime(isExternalPredecessor(info) ? info.startDate : predecessor?.attributes.StartDate);
        const predecessorDue = toUniversalDateTime(isExternalPredecessor(info) ? info.dueDate : predecessor?.attributes.DueDate);

        // Task 13311: in keep mode a dictated START for a zero-duration successor stays at the end of the working
        // range (the anchor's finish instant) instead of snapping forward. With time-of-day carried on Start/Due,
        // the old IsTaskPlacedOnTheEndOfTheDay recursion is gone — "end of working range" is just the anchor
        // instant landing on a range boundary, which dictate() keeps when keepAtRangeEnd is set.
        const keepAtRangeEnd = !shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay && TaskScheduleCalculator.IsZeroLengthTask(currentTask);

        if (info.type === PredecessorType.FinishToStart) {
            return { startDate: dictate(predecessorDue ?? predecessorStart, info.lag, true, keepAtRangeEnd, calendarSettings, globalCalendar) };
        }
        if (info.type === PredecessorType.FinishToFinish) {
            return { dueDate: dictate(predecessorDue ?? predecessorStart, info.lag, false, false, calendarSettings, globalCalendar) };
        }
        if (info.type === PredecessorType.StartToStart) {
            return { startDate: dictate(predecessorStart ?? predecessorDue, info.lag, true, keepAtRangeEnd, calendarSettings, globalCalendar) };
        }
        if (info.type === PredecessorType.StartToFinish) {
            return { dueDate: dictate(predecessorStart ?? predecessorDue, info.lag, false, false, calendarSettings, globalCalendar) };
        }
        return {};
    }

    public static GetTaskDictatedDates(
        task: ITask,
        tasksMap: Dictionary<LoadedTask>,
        globalCalendar: CalendarDataSet,
        useResourceCalendar: boolean,
        resources: Dictionary<Resource>,
        calendarsById: Dictionary<CalendarDataSet>,
        shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay: boolean = true): DictatedDates {
        if (!task.attributes.Predecessor?.length) {
            return {};
        }

        if (!isTaskPredecessorHierarchyValid(task, tasksMap)) {
            return {};
        }

        const assigneeCalendars = getAssigneeCalendars(useResourceCalendar, task.attributes.AssignedTo, globalCalendar, resources, calendarsById);
        return TaskScheduleCalculator.GetDictatedDates(
            task.attributes.Predecessor,
            tasksMap,
            globalCalendar,
            assigneeCalendars,
            shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay,
            task);
    }

    public static GetDictatedDates(
        predecessors: IPredecessorInfo[] | undefined,
        tasksMap: Dictionary<LoadedTask>,
        globalCalendar: CalendarDataSet,
        calendarSettings: CalendarDataSet[],
        shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay: boolean = true,
        successor?: LoadedTask): DictatedDates {
        if (!predecessors) {
            return {};
        }

        const dates = predecessors
            .map(_ => TaskScheduleCalculator.GetDatesDictatedByPredecessor(
                tasksMap[_.id],
                _,
                globalCalendar,
                calendarSettings,
                shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay,
                successor));
        const dictatedDates = { startDate: utils.max(dates.map(_ => _.startDate)), dueDate: utils.max(dates.map(_ => _.dueDate)) };

        if (dictatedDates.startDate && dictatedDates.dueDate && dictatedDates.startDate > dictatedDates.dueDate) {
            // Bug 17135: the successor's own calendars own that day's end — mirrors ReconcileDictatedDates.
            dictatedDates.dueDate = dictatedDates.startDate.clone()
                .setTimeOfDay(utils.dayEndMinutesFor(dictatedDates.startDate, calendarSettings, globalCalendar));
        }
        return dictatedDates;
    }

    // US 16555: lag is a decimal number of working days — the signed working-day duration between the
    // dependency's lag-0 anchor (currentDate) and the target instant (expectedDate). This is the exact
    // inverse of the hybrid lag applied in dictateDate/applyLag, so e.g. a predecessor finishing 0.75 of the
    // way through a day yields lag 0.25 when the successor is targeted at the next working day's start.
    public static GetLag(expectedDate: Date, currentDate: Date, ...calendars: CalendarDataSet[]): number {
        const globalCalendar = calendars[0];
        if (expectedDate.getTime() > currentDate.getTime()) {
            return utils.getDurationBetween(currentDate, expectedDate, calendars, globalCalendar);
        } else if (expectedDate.getTime() < currentDate.getTime()) {
            return -utils.getDurationBetween(expectedDate, currentDate, calendars, globalCalendar);
        }
        return 0;
    }

    private static IsZeroLengthTask(task?: LoadedTask): boolean {
        return task?.attributes?.Duration === 0;
    }
}

const tenMultiplier: number = 10;

export class TaskProgressCalculator {

    public static RecalculateByProgress(project: ProjectInfo, task: ITask, value: number | null): Partial<ITaskAttrs> | undefined {
        if (!task.isAutoMode
            || project.settings.progressCalculationType !== ProgressCalculationType.Effort
            || !task.attributes.Effort
            || value === null) {
            return undefined;
        }
        let multiplayer = this.pointMultiplayer(task.attributes.Effort);
        const completedWork = this.Round(value * task.attributes.Effort * multiplayer) / (multiplayer * HUNDRED_PCT);
        multiplayer = this.pointMultiplayer(task.attributes.Effort, completedWork);
        return {
            CompletedWork: completedWork,
            RemainingWork: this.Round(task.attributes.Effort * multiplayer - completedWork * multiplayer) / multiplayer,
        }
    }

    public static RecalculateByCompletedWork(project: ProjectInfo, task: ITask, value: number | null): Partial<ITaskAttrs> | undefined {
        if (!task.isAutoMode
            || project.settings.progressCalculationType !== ProgressCalculationType.Effort
            || !task.attributes.Effort
            || value === null) {
            return undefined;
        }

        if (value > task.attributes.Effort) {
            return {
                Effort: value,
                Progress: HUNDRED_PCT,
                RemainingWork: 0,
            };
        }

        const progress = this.Round(value * HUNDRED_PCT / task.attributes.Effort);
        const multiplayer = this.pointMultiplayer(task.attributes.Effort, value);
        const remainingWork = multiplayer ? this.Round(task.attributes.Effort * multiplayer - value * multiplayer) / multiplayer : 0;
        return {
            Progress: adjustProgress(progress),
            RemainingWork: remainingWork
        };
    }

    public static RecalculateByEffort(project: ProjectInfo, task: ITask, value: number | null): Partial<ITaskAttrs> | undefined {
        if (!task.isAutoMode
            || project.settings.progressCalculationType !== ProgressCalculationType.Effort
            || task.attributes.CompletedWork === undefined
            || value === null) {
            return undefined;
        }

        if (value < task.attributes.CompletedWork) {
            return {
                CompletedWork: value,
                Progress: HUNDRED_PCT,
                RemainingWork: 0,
            };
        }

        const multiplayer = this.pointMultiplayer(task.attributes.CompletedWork, value);
        const remainingWork = this.Round(value * multiplayer - task.attributes.CompletedWork * multiplayer) / multiplayer;
        const progress = value ? this.Round(task.attributes.CompletedWork * HUNDRED_PCT / value) : 0;
        return {
            RemainingWork: remainingWork,
            Progress: adjustProgress(progress)
        }
    }

    public static RecalculateByRemainingWork(project: ProjectInfo, task: ITask, value: number | null): Partial<ITaskAttrs> | undefined {
        if (!task.isAutoMode
            || project.settings.progressCalculationType !== ProgressCalculationType.Effort
            || task.attributes.CompletedWork === undefined
            || value === null) {
            return undefined;
        }

        const multiplayer = this.pointMultiplayer(task.attributes.CompletedWork, value);
        const effort = this.Round(task.attributes.CompletedWork * multiplayer + value * multiplayer) / multiplayer;
        const progress = effort ? this.Round(task.attributes.CompletedWork * HUNDRED_PCT / effort) : 0;
        return {
            Effort: effort,
            Progress: adjustProgress(progress)
        }
    }

    private static Round(value: number): number {
        return Math.floor(value);
    }

    private static countDecimals = (values: number[]): number =>
        Math.max.apply(null, values.map(value => Math.floor(value) === value ? 0 : value?.toString().split(".")[1].length || 0));

    private static pointMultiplayer = (...values: number[]): number =>
        Math.pow(tenMultiplier, TaskProgressCalculator.countDecimals(values));
}

export function isAllowedAsPredecessorByHierarchy<T extends IBaseEntity & { hierarchy?: { parentIds?: string[]; } }>(task: T, maybePredecessor: T) {
    return !(task?.hierarchy?.parentIds?.includes(maybePredecessor.id) || maybePredecessor.hierarchy?.parentIds?.includes(task.id));
}

export function isTaskPredecessorHierarchyValid(task: ITask, tasksMap: Dictionary<LoadedTask>) {
    return (task.attributes.Predecessor ?? [])
        .map(_ => tasksMap[_.id])
        .filter(notUndefined)
        .every(_ => isAllowedAsPredecessorByHierarchy(task, _));
};
